import {type FilterConfiguration, type FilterConfigurationLineItem} from "~/components/ottopilotDataViewer/filterConfiguration";
import {FilterOperation} from "@vesta/vos-types/modules/workspaceLevel/domain/FilterOperation";
import {LIST_INQUEUE_ASSIGNMENT_STATUS} from "~/components/ottopilotDataViewer/listStatusConfig";
import {generateUuid} from "@vesta/global-common-typescript/utilities/utilities";

export type InQueueAssignmentStatus = "assigned" | "unassigned";

function normalizeInQueueAssignmentStatusToken(token: string): InQueueAssignmentStatus | null {
    const lower = token.trim().toLowerCase();
    if (lower === "assigned" || lower === LIST_INQUEUE_ASSIGNMENT_STATUS.assigned) {
        return "assigned";
    }
    if (lower === "unassigned" || lower === LIST_INQUEUE_ASSIGNMENT_STATUS.unassigned) {
        return "unassigned";
    }
    return null;
}

export function isInQueueLeadOwnerAssignmentFilterLineItem(
    lineItem: FilterConfigurationLineItem,
    leadOwnerColumnId: string,
): boolean {
    if (lineItem.columnId !== leadOwnerColumnId) {
        return false;
    }
    if (lineItem.operation === FilterOperation.equals && lineItem.value === null) {
        return true;
    }
    if (lineItem.operation === FilterOperation.notEquals && lineItem.value === null) {
        return true;
    }
    return false;
}

export function buildInQueueLeadOwnerFilterLineItemFromStatusId(
    statusId: string,
    leadOwnerColumnId: string,
): FilterConfigurationLineItem | null {
    const normalized = normalizeInQueueAssignmentStatusToken(statusId);
    if (normalized === null) {
        return null;
    }
    return {
        columnId: leadOwnerColumnId,
        operation: normalized === "assigned" ? FilterOperation.notEquals : FilterOperation.equals,
        value: null,
    };
}

/** Reads Assigned/Unassigned from UI status filter line items (including legacy synthetic values). */
export function resolveInQueueAssignmentFromStatusFilterLineItems(
    statusLineItems: ReadonlyArray<FilterConfigurationLineItem>,
): InQueueAssignmentStatus | "both" | null {
    let wantsAssigned = false;
    let wantsUnassigned = false;

    for (const lineItem of statusLineItems) {
        const tokens =
            lineItem.operation === FilterOperation.in
                ? lineItem.values
                : "value" in lineItem
                  ? [String(lineItem.value)]
                  : [];
        for (const token of tokens) {
            const resolved = normalizeInQueueAssignmentStatusToken(token);
            if (resolved === "assigned") {
                wantsAssigned = true;
            }
            if (resolved === "unassigned") {
                wantsUnassigned = true;
            }
        }
    }

    if (wantsAssigned === true && wantsUnassigned === true) {
        return "both";
    }
    if (wantsAssigned === true) {
        return "assigned";
    }
    if (wantsUnassigned === true) {
        return "unassigned";
    }
    return null;
}

export function buildInQueueLeadOwnerBackendLineItemsFromStatusFilters(
    statusLineItems: ReadonlyArray<FilterConfigurationLineItem>,
    leadOwnerColumnId: string,
): Array<FilterConfigurationLineItem> {
    const resolved = resolveInQueueAssignmentFromStatusFilterLineItems(statusLineItems);
    if (resolved === null || resolved === "both") {
        return [];
    }
    const lineItem = buildInQueueLeadOwnerFilterLineItemFromStatusId(resolved, leadOwnerColumnId);
    if (lineItem === null) {
        return [];
    }
    return [lineItem];
}

export function stripInQueueLeadOwnerAssignmentFilters(
    lineItems: FilterConfiguration["lineItems"],
    leadOwnerColumnId: string,
): FilterConfiguration["lineItems"] {
    return lineItems.filter(
        (lineItem) => isInQueueLeadOwnerAssignmentFilterLineItem(lineItem, leadOwnerColumnId) === false,
    );
}

/** Keeps sidebar Assigned/Unassigned null checks when toolbar updates the same owner column. */
export function removeInQueueLeadOwnerColumnFiltersExceptAssignment(
    lineItems: FilterConfiguration["lineItems"],
    columnId: string,
): FilterConfiguration["lineItems"] {
    return lineItems.filter((lineItem) => {
        if (lineItem.columnId !== columnId) {
            return true;
        }
        return isInQueueLeadOwnerAssignmentFilterLineItem(lineItem, columnId) === true;
    });
}

/** Exact line items to keep on Clear (scaffold slice only — not agent/owner `in` filters). */
export function getInQueuePreservedAssignmentFilterLineItems(args: {
    leadOwnerColumnId: string | null | undefined;
    activeStatusId: string | null;
}): Array<FilterConfigurationLineItem> {
    if (
        args.leadOwnerColumnId === null ||
        args.leadOwnerColumnId === undefined ||
        args.leadOwnerColumnId === "" ||
        args.activeStatusId === null
    ) {
        return [];
    }
    const lineItem = buildInQueueLeadOwnerFilterLineItemFromStatusId(
        args.activeStatusId,
        args.leadOwnerColumnId,
    );
    if (lineItem === null) {
        return [];
    }
    return [lineItem];
}

/** Sidebar In Queue slice → API filter on lead owner column (null = Unassigned, not null = Assigned). */
export function applyInQueueAssignmentStatusIdToFilterConfiguration(
    filterConfiguration: FilterConfiguration,
    leadOwnerColumnId: string,
    statusId: string | null,
): FilterConfiguration {
    const withoutAssignment = stripInQueueLeadOwnerAssignmentFilters(
        filterConfiguration.lineItems,
        leadOwnerColumnId,
    );

    if (statusId === null) {
        return {
            revisionId: generateUuid(),
            lineItems: withoutAssignment,
        };
    }

    const assignmentLineItem = buildInQueueLeadOwnerFilterLineItemFromStatusId(statusId, leadOwnerColumnId);
    if (assignmentLineItem === null) {
        return {
            revisionId: generateUuid(),
            lineItems: withoutAssignment,
        };
    }

    return {
        revisionId: generateUuid(),
        lineItems: [...withoutAssignment, assignmentLineItem],
    };
}

export function inQueueLeadOwnerAssignmentFilterMatchesStatusId(
    filterConfiguration: FilterConfiguration,
    leadOwnerColumnId: string,
    statusId: string | null,
): boolean {
    const expected =
        statusId === null ? null : buildInQueueLeadOwnerFilterLineItemFromStatusId(statusId, leadOwnerColumnId);
    const current = filterConfiguration.lineItems.find((lineItem) =>
        isInQueueLeadOwnerAssignmentFilterLineItem(lineItem, leadOwnerColumnId),
    );
    if (expected === null && current === undefined) {
        return true;
    }
    if (expected === null || current === undefined) {
        return false;
    }
    return expected.operation === current.operation;
}

export function partitionInQueueFiltersForBackend(args: {
    lineItems: FilterConfiguration["lineItems"];
    statusFilterColumnIds: ReadonlySet<string>;
    leadOwnerColumnId: string | null;
}): {
    backendLineItems: FilterConfiguration["lineItems"];
    inQueueAssignmentBackendLineItems: Array<FilterConfigurationLineItem>;
} {
    const statusLineItems = args.lineItems.filter((lineItem) =>
        args.statusFilterColumnIds.has(lineItem.columnId),
    );
    const leadOwnerColumnId = args.leadOwnerColumnId;
    const directLeadOwnerAssignmentLineItems =
        leadOwnerColumnId === null
            ? []
            : args.lineItems.filter((lineItem) =>
                  isInQueueLeadOwnerAssignmentFilterLineItem(lineItem, leadOwnerColumnId),
              );
    const assignmentFromStatusLineItems =
        leadOwnerColumnId === null
            ? []
            : buildInQueueLeadOwnerBackendLineItemsFromStatusFilters(statusLineItems, leadOwnerColumnId);
    const inQueueAssignmentBackendLineItems =
        directLeadOwnerAssignmentLineItems.length > 0
            ? directLeadOwnerAssignmentLineItems
            : assignmentFromStatusLineItems;

    const backendLineItems = args.lineItems.filter((lineItem) => {
        if (args.statusFilterColumnIds.has(lineItem.columnId) === true) {
            return false;
        }
        if (
            leadOwnerColumnId !== null &&
            isInQueueLeadOwnerAssignmentFilterLineItem(lineItem, leadOwnerColumnId) === true
        ) {
            return false;
        }
        return true;
    });

    return {
        backendLineItems,
        inQueueAssignmentBackendLineItems,
    };
}
